import { Component, Input, Output, EventEmitter, inject, ChangeDetectionStrategy } from '@angular/core';
import { GenericRosterComponent } from '../generic-roster/generic-roster.component';
import { StudentCardComponent } from './student-card.component';
import {
  School,
  SchoolRosterFilter,
  SchoolTermGradeDto,
  SchoolGradeYearDto,
  AttendanceType,
  SchoolRosterStudent
} from '@k12/models';
import { SchoolsService } from '@k12/school-services';
import {
  DEFAULT_ROSTER_COLUMN_DEFINITIONS,
  DEFAULT_ROSTER_DISPLAYED_COLUMNS,
  DEFAULT_ROSTER_COLUMN_TO_API_FIELD_MAP
} from '../roster-column-definitions';
import { ColumnDefinition } from '@k12/tables';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-endorsed-roster',
  template: `
    <lib-generic-roster
      [currentSchool]="currentSchool"
      [editable]="editable"
      [showAdminActions]="showAdminActions"
      [disableBackwardMove]="disableBackwardMove"
      [schoolTermId]="schoolTermId"
      [termName]="termName"
      [academicYearId]="academicYearId"
      [reloadTrigger]="reloadTrigger"
      [studentCardComponent]="studentCardComponent"
      [loadStudentsQuery]="loadStudentsQuery"
      [exportQuery]="exportQuery"
      [importQuery]="importQuery"
      [rosterTitle]="rosterTitle"
      [columnDefinitions]="columnDefinitions"
      [displayedColumns]="displayedColumns"
      [columnToApiFieldMap]="columnToApiFieldMap"
      [schoolTermGrades]="schoolTermGrades"
      [schoolGradeYears]="schoolGradeYears"
      [attendanceTypes]="attendanceTypes"
      [emptyStateMessage]="emptyStateMessage"
      [searchTerm]="searchTerm"
      (countChanged)="countChanged.emit($event)"
      (saved)="saved.emit($event)"
      (reverted)="reverted.emit($event)"
      (withdrawalRequested)="onWithdrawal($event)">
    </lib-generic-roster>
  `,
  standalone: true,
  // eslint-disable-next-line @angular-eslint/component-class-suffix
  imports: [GenericRosterComponent]
})
export class EndorsedRosterComponent {
  @Input() currentSchool: School | null = null;
  @Input() editable = false;
  @Input() showAdminActions = false;
  @Input() disableBackwardMove = false;
  @Input() schoolTermId: string | null = null;
  @Input() termName: string | null = null;
  @Input() academicYearId: number | null = null;
  @Input() reloadTrigger: number = 0;
  @Input() schoolTermGrades: SchoolTermGradeDto[] = [];
  @Input() schoolGradeYears: SchoolGradeYearDto[] = [];
  @Input() attendanceTypes: AttendanceType[] = [];
  @Input() emptyStateMessage: string = 'No endorsed students found.';
  @Input() searchTerm: string = '';
  @Output() countChanged = new EventEmitter<number>();
  @Output() saved = new EventEmitter<string>();
  @Output() reverted = new EventEmitter<string>();
  @Output() withdrawalRequested = new EventEmitter<SchoolRosterStudent>();

  get studentCardComponent() {
    return StudentCardComponent;
  }

  // Extend default columns with Household Endorsed column
  columnDefinitions: ColumnDefinition[] = [
    ...DEFAULT_ROSTER_COLUMN_DEFINITIONS,
    { name: 'isParentEndorsed', header: 'Household Endorsed?', width: 8, sortable: true }
  ];

  displayedColumns: string[] = [...DEFAULT_ROSTER_DISPLAYED_COLUMNS, 'isParentEndorsed'];

  columnToApiFieldMap: { [key: string]: string } = {
    ...DEFAULT_ROSTER_COLUMN_TO_API_FIELD_MAP,
    isParentEndorsed: 'isParentEndorsed'
  };

  protected onWithdrawal(student: SchoolRosterStudent): void {
    this.withdrawalRequested.emit(student);
  }

  private readonly schoolsService: SchoolsService = inject(SchoolsService);
  loadStudentsQuery = (filters: SchoolRosterFilter) => this.schoolsService.getEndorsedRosters(filters);
  exportQuery = (mini: boolean, schoolTermId?: string | null) =>
    this.schoolsService.exportEndorsedRoster(this.currentSchool!.programPartnerId!, mini, schoolTermId, this.academicYearId);
  importQuery = (file: File, schoolTermId?: string | null) =>
    this.schoolsService.importRoster(this.currentSchool!.programPartnerId!, file, schoolTermId);
  rosterTitle = 'Endorsed';
}
